import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { Toaster } from "react-hot-toast";
import {
    Link,
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    isRouteErrorResponse,
    useRouteError,
} from "react-router";
import type { LinksFunction, MetaFunction } from "react-router";

import "@/app/globals.css";
import { ThemeProvider } from "@/components/ThemeProvider";
import { StatusBarTheme } from "@/components/theme/StatusBarTheme";
import { Button } from "@/components/ui/button";
import ApiQueryProvider from "@/providers/api-query-provider";
import { AppDataProvider } from "@/providers/app-data-provider";

import { initI18n } from "./lib/i18n.client";

export const links: LinksFunction = () => [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
    },
    {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap",
    },
    { rel: "manifest", href: "/manifest.json", crossOrigin: "use-credentials" as const },
];

export const meta: MetaFunction = () => {
    return [{ title: "Kwik Nkap" }, { name: "description", content: "The Future of payments." }];
};

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en" suppressHydrationWarning>
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="theme-color" content="#0C8C7E" />
                <StatusBarTheme />
                <Meta />
                <Links />
            </head>
            <body className="font-sans antialiased">
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    useEffect(() => {
        initI18n();
    }, []);

    return (
        <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem
            disableTransitionOnChange
            storageKey="knp-theme"
        >
            <ApiQueryProvider>
                <AppDataProvider>
                    <Outlet />
                </AppDataProvider>
                <Toaster />
            </ApiQueryProvider>
        </ThemeProvider>
    );
}

export function ErrorBoundary() {
    const error = useRouteError();
    const prefersReduced = useReducedMotion();

    useEffect(() => {
        Sentry.captureException(error);
    }, [error]);

    const isRouteError = isRouteErrorResponse(error);
    const title = isRouteError
        ? `${error.status} ${error.statusText}`
        : "Oops! Something went wrong";
    const message = isRouteError
        ? error.data || "The page you are looking for could not be found."
        : "We're sorry for the inconvenience. Our team has been notified and is working on a fix.";

    return (
        <div className="bg-background">
            <div className="flex min-h-screen flex-col items-center justify-center p-4 text-center md:p-8">
                <motion.div
                    initial={{ opacity: 0, y: prefersReduced ? 0 : 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: prefersReduced ? 0 : 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md"
                >
                    <div className="relative mb-8 h-16">
                        <img
                            src="https://kn-prod-store.fra1.cdn.digitaloceanspaces.com/img/logo/logotype_black.png"
                            alt="Kwik Nkap"
                            className="mx-auto h-full w-auto object-contain dark:invert"
                        />
                    </div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{
                            delay: prefersReduced ? 0 : 0.3,
                            duration: prefersReduced ? 0 : 0.6,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                    >
                        <h1 className="mb-2 text-2xl font-bold">{title}</h1>
                        <p className="text-muted-foreground mb-8">{message}</p>

                        <div className="flex flex-col justify-center gap-4 sm:flex-row">
                            <Button
                                onClick={() => window.location.reload()}
                                className="flex items-center gap-2"
                            >
                                <RefreshCw size={16} /> Try Again
                            </Button>
                            <Button variant="outline" asChild>
                                <Link to="/" className="flex items-center gap-2">
                                    <ArrowLeft size={16} /> Return Home
                                </Link>
                            </Button>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
}
